import { z } from 'zod';

/**
 * PRE-190: API contracts for the product document now that it lives in D1.
 *
 * Two catalogs, one document. Business rules (`BR-<n>`) say what the product
 * must do; the blueprint says whether we actually do it. They used to be a
 * markdown file and a TypeScript module bundled into the panel at build time,
 * which made every product-owner edit a code change and a deploy. These are the
 * shapes the panel writes over the API instead.
 *
 * Ids are stable and never reused — `// BR-<n>` comments in code and `trace.br`
 * anchors in the blueprint point back by id, so recycling one silently
 * reattributes an invariant.
 */

const flowStatusSchema = z.enum(['ready', 'todo', 'gap']);

/** Repo-relative source anchor, optionally `:line`. The CI guardrail resolves
 *  each one against the repo tree, so a moved file fails the build. */
const anchorSchema = z.string().trim().min(1).max(300);

const businessRuleIdSchema = z
  .string()
  .trim()
  .regex(/^BR-\d+$/, 'Rule id must look like BR-12');

const traceSchema = z.object({
  br: z.array(businessRuleIdSchema).max(50).default([]),
  code: z.array(anchorSchema).max(50).default([]),
  tests: z.array(anchorSchema).max(50).default([]),
});

// ── Business rules ────────────────────────────────────────────────────────────

export const businessRuleConfigStatusSchema = z.enum([
  'migrated',
  'hybrid',
  'in-code',
  'invariant',
  'unknown',
]);

/** Runtime-config posture of a rule. */
export type BusinessRuleConfigStatus = z.infer<typeof businessRuleConfigStatusSchema>;

/**
 * Which question a rule answers.
 *
 * `product` — what the product does, decided by the product owner and felt by a
 * learner. `platform` — how the system carries it: envelopes, idempotency,
 * cascade deletes, telemetry. Both are real invariants; they are separated
 * because they are read by different people, and one list of 85 makes the
 * product owner scroll past request-id plumbing to reach the scoring formula.
 */
export const businessRuleTypeSchema = z.enum(['product', 'platform']);
export type BusinessRuleType = z.infer<typeof businessRuleTypeSchema>;

/**
 * Who put the `type` there: nobody yet, a model, or a person.
 *
 * Same separation the RTM review keeps between a suggestion and a verdict. A
 * label a model wrote is worth having — it saves the product owner reading 85
 * rules — but it is not a decision until someone confirms it, and a column that
 * cannot tell the two apart turns "which rules are product rules" into whatever
 * the model happened to answer.
 */
export const businessRuleTypeStatusSchema = z.enum(['unset', 'suggested', 'confirmed']);
export type BusinessRuleTypeStatus = z.infer<typeof businessRuleTypeStatusSchema>;

/**
 * A stored business rule as the API returns it.
 *
 * Until PRE-190 this shape came out of a markdown parser reading
 * `docs/business-rules.md`; the catalog is a database table now, so the type is
 * the API contract rather than the parse result of a document.
 */
export interface BusinessRule {
  /** Stable id, e.g. `BR-1`. Never reused. */
  id: string;
  /** Numeric part of the id, for sorting. */
  num: number;
  /** The process/area group the rule lives under (e.g. `Assessment`, `Scoring`). */
  area: string;
  /** Whether the rule is a product decision or the plumbing that carries one. */
  type: BusinessRuleType;
  /**
   * PRE-126: when somebody last read this rule and said it is right, who they
   * were, and the text they read. Null throughout = never reviewed.
   *
   * `reviewedRule` is what the panel compares against `rule`: when they differ,
   * the sentence has been rewritten since and the review is stale.
   */
  reviewedAt: string | null;
  reviewedBy: string | null;
  reviewedRule: string | null;
  /**
   * PRE-102: what the reviewer decided, and their reason when they rejected.
   * Null throughout = never reviewed. A `rejected` row always carries a note —
   * the API refuses one without.
   */
  reviewVerdict: BusinessRuleVerdict | null;
  reviewNote: string | null;
  /** Priority label (`P0` = critical … `P2` = minor). */
  priority: string;
  /** Lifecycle status (`Done`, `Planned`, …). */
  status: string;
  /** The invariant itself. */
  rule: string;
  /** Enforcing reference(s): `file:line` code refs and/or a doc pointer. */
  source: string;
  /** Runtime-config note, or null. */
  config: string | null;
  /** Decoded runtime-config posture. */
  configStatus: BusinessRuleConfigStatus;
}

/** Everything about a rule except its id, which is immutable once assigned. */
export const businessRuleInputSchema = z.object({
  area: z.string().trim().min(1).max(80),
  type: businessRuleTypeSchema.default('product'),
  priority: z.string().trim().min(1).max(20),
  status: z.string().trim().min(1).max(40),
  rule: z.string().trim().min(1).max(2000),
  source: z.string().trim().max(1000).default(''),
  config: z.string().trim().max(1000).nullable().default(null),
  configStatus: businessRuleConfigStatusSchema.default('unknown'),
});

export type BusinessRuleInput = z.infer<typeof businessRuleInputSchema>;

/**
 * The type decision on its own — what the panel's tick sends.
 *
 * Separate from `businessRuleInputSchema` because confirming a label is not
 * editing a rule: it needs no rule text, no source, no priority, and making the
 * caller round-trip all of them to agree with one word is how a decision ends up
 * silently rewriting the invariant next to it. `confirmed: false` withdraws the
 * decision and hands the row back to the model's suggestion, the way unticking
 * an RTM row withdraws consent rather than recording a verdict of its own.
 */
/**
 * PRE-126: the panel's only write on a rule — "I have read this and it is right".
 *
 * `false` withdraws it. A review nobody stands behind is not a weaker review, it
 * is none, so withdrawing clears the row rather than leaving a dated trace that
 * reads like one.
 */
export const businessRuleVerdictSchema = z.enum(['confirmed', 'rejected']);
export type BusinessRuleVerdict = z.infer<typeof businessRuleVerdictSchema>;

/**
 * PRE-102: the review can now say no, and a no has to carry its reasoning.
 *
 * `reviewed: false` still withdraws, clearing the row. `verdict` defaults to
 * `confirmed` so every client that predates this — including the panel build
 * being replaced in the same change — keeps meaning what it meant.
 *
 * The `note` requirement is deliberately the same rule Traceability applies to
 * a non-`covered` verdict (`needsNote`): a rejection is a claim that the
 * catalog is wrong about the product, and a claim like that is worthless
 * without the sentence explaining it. A confirmation may carry a note too; it
 * is simply not required, because "this is right" needs no argument beyond the
 * rule it is agreeing with.
 */
export const businessRuleReviewSchema = z
  .object({
    reviewed: z.boolean(),
    verdict: businessRuleVerdictSchema.default('confirmed'),
    note: z.string().trim().max(2000).optional(),
  })
  .refine((v) => !(v.reviewed && v.verdict === 'rejected') || !!v.note, {
    message: 'A rejected review must carry a note explaining why.',
    path: ['note'],
  });
export type BusinessRuleReview = z.infer<typeof businessRuleReviewSchema>;

export const createBusinessRuleInputSchema = businessRuleInputSchema.extend({
  /** Caller-chosen id so the operator controls the numbering; must be free. */
  id: businessRuleIdSchema,
});

export type CreateBusinessRuleInput = z.infer<typeof createBusinessRuleInputSchema>;

// ── Blueprint content ─────────────────────────────────────────────────────────

/** Doc-level metadata. `verifiedAt` is the re-walk stamp the staleness warning
 *  reads, so it is edited deliberately rather than touched on every save. */
export const blueprintMetaInputSchema = z.object({
  title: z.string().trim().min(1).max(200),
  owner: z.string().trim().min(1).max(200),
  verifiedAt: z
    .string()
    .trim()
    .regex(/^\d{4}-\d{2}-\d{2}$/, 'Use an ISO date (YYYY-MM-DD)'),
  staleAfterDays: z.number().int().min(1).max(3650),
  adr: z.string().trim().max(300).default(''),
  ruleCatalog: z.string().trim().max(300).default(''),
  decisionDate: z.string().trim().max(40).nullable().default(null),
});

export type BlueprintMetaInput = z.infer<typeof blueprintMetaInputSchema>;

/**
 * PRE-125: the re-walk stamp, alone.
 *
 * The meta row mixes seeded content (title, owner, the staleness window) with
 * `verifiedAt`, which is a person saying they walked the document against the
 * code. The content is authored in the repo now, so the only field this endpoint
 * still takes is the stamp — and taking just the stamp is also the honest shape:
 * certifying a walk was never meant to ride along with an unrelated save.
 */
export const blueprintStampInputSchema = z
  .object({
    verifiedAt: z
      .string()
      .trim()
      .regex(/^\d{4}-\d{2}-\d{2}$/, 'Use an ISO date (YYYY-MM-DD)'),
  })
  // Strict on purpose. Zod would otherwise drop a `title` sent alongside the
  // stamp, and the caller would be told the save succeeded — a quieter version
  // of exactly the ghost write PRE-125 closed.
  .strict();

export type BlueprintStampInput = z.infer<typeof blueprintStampInputSchema>;

export const blueprintPhaseInputSchema = z.object({
  num: z.string().trim().min(1).max(40),
  title: z.string().trim().min(1).max(200),
  sub: z.string().trim().max(4000).default(''),
  caption: z.string().trim().max(300).default(''),
  /** Mermaid source; rendered verbatim by the panel. */
  chart: z.string().max(20000).default(''),
  notes: z
    .array(
      z.object({
        kind: z.enum(['info', 'warn']),
        title: z.string().trim().min(1).max(200),
        text: z.string().trim().min(1).max(4000),
      }),
    )
    .max(20)
    .default([]),
});

export type BlueprintPhaseInput = z.infer<typeof blueprintPhaseInputSchema>;

export const blueprintStepInputSchema = z.object({
  phaseId: z.string().trim().min(1).max(80),
  who: z.string().trim().min(1).max(120),
  what: z.string().trim().min(1).max(2000),
  rule: z.string().trim().max(2000).default(''),
  status: flowStatusSchema,
  trace: traceSchema.default({ br: [], code: [], tests: [] }),
});

export type BlueprintStepInput = z.infer<typeof blueprintStepInputSchema>;

export const createBlueprintStepInputSchema = blueprintStepInputSchema.extend({
  /** `F<phase>.<n>`; the matrix and every coverage verdict key on this. */
  id: z
    .string()
    .trim()
    .regex(/^F\d+\.\d+$/, 'Step id must look like F1.2'),
});

export const blueprintBacklogInputSchema = z.object({
  step: z.string().trim().min(1).max(200),
  status: flowStatusSchema,
  note: z.string().trim().min(1).max(2000),
  /** Flow step ids this item unblocks. */
  steps: z.array(z.string().trim().max(20)).max(50).default([]),
  trace: traceSchema.default({ br: [], code: [], tests: [] }),
});

export type BlueprintBacklogInput = z.infer<typeof blueprintBacklogInputSchema>;

export const createBlueprintBacklogInputSchema = blueprintBacklogInputSchema.extend({
  id: z
    .string()
    .trim()
    .regex(/^B-\d+$/, 'Backlog id must look like B-07'),
});

export const blueprintSliceInputSchema = z.object({
  title: z.string().trim().min(1).max(200),
  outcome: z.string().trim().min(1).max(2000),
  /** Backlog ids; the guardrail verifies every one resolves. */
  items: z.array(z.string().trim().max(20)).max(100).default([]),
  rationale: z.string().trim().max(4000).default(''),
});

export type BlueprintSliceInput = z.infer<typeof blueprintSliceInputSchema>;

export const createBlueprintSliceInputSchema = blueprintSliceInputSchema.extend({
  id: z
    .string()
    .trim()
    .regex(/^S-\d+$/, 'Slice id must look like S-3'),
});

/**
 * A product rule. Note there is no id field: rtmkit derives the `R-<nn>` matrix
 * id from the rule's position, and coverage verdicts are stored against that
 * derived id. The API therefore never reorders rules — it appends.
 */
export const blueprintRuleInputSchema = z.object({
  rule: z.string().trim().min(1).max(200),
  says: z.string().trim().min(1).max(2000),
  why: z.string().trim().max(2000).default(''),
  br: z.array(businessRuleIdSchema).max(50).default([]),
  /**
   * PRE-81: code anchors, the same shape steps and backlog items carry.
   *
   * Rules had none until now, so every `R-<nn>` reached the semantic review with
   * nothing to read and came back "no anchor" — while claiming to be built. They
   * are the rows a machine checks most reliably, because a rule like "the
   * graduation threshold is 70" is a constant to find and compare rather than a
   * behaviour to interpret.
   *
   * Optional, and left empty by default: which line enforces an invariant is a
   * product judgement, so these are filled in from the panel rather than guessed.
   */
  code: z.array(anchorSchema).max(50).default([]),
  tests: z.array(anchorSchema).max(50).default([]),
});

export type BlueprintRuleInput = z.infer<typeof blueprintRuleInputSchema>;
